import { buildImageSrc } from '@repo/ui/src/functions/image-functions-aspect-ratio';
import { formatCookTime } from '@repo/ui/src/functions/recipe-functions';
import RatingStars from '@repo/ui/src/components/RatingStars';
import Image from '@repo/ui/src/components/Image';
import { Anchor } from '@repo/ui/src/components/Anchor';
import { classNames } from '../../utils/classNames';
import type { RecipeReference } from './types';

interface RecipeCardProps {
  recipe: RecipeReference;
  title: string;
  theme: 'dark' | 'light';
}

export function RecipeCard({ recipe, title, theme }: RecipeCardProps) {
  const { _meta, recipeMetaSection, assets, ratingsAndReviews } = recipe;
  if (!recipeMetaSection) return null;

  const isDark = theme === 'dark';
  const imageUrl = buildImageSrc(assets?.length ? assets[0] : null, 400, 4, 3);
  const link = _meta?.deliveryKey;
  const ratingSummary = ratingsAndReviews?.ratingSummary;
  const totalTime = (recipeMetaSection.cookTime || 0) + (recipeMetaSection.prepTime || 0);
  const timeDisplay = formatCookTime(totalTime);

  return (
    <div className="mt-6 print:hidden">
      <h4
        className={classNames(
          'mb-4 tra-text-web-sub-2',
          isDark ? 'text-neutral-100 print:text-neutral-600' : 'text-neutral-600',
        )}
      >
        {title}
      </h4>
      <Anchor
        link={link}
        className={classNames(
          'flex overflow-hidden border',
          isDark ? 'border-neutral-500' : 'border-transparent-light-200',
        )}
        label={recipeMetaSection.title}
      >
        {imageUrl && (
          <div className="relative w-2/5 shrink-0 aspect-[4/3]">
            <Image
              src={imageUrl}
              alt={recipeMetaSection.title || ''}
              layout="fill"
              objectFit="cover"
            />
          </div>
        )}
        <div className="flex flex-col justify-center gap-2 px-5 py-6">
          <span
            className={classNames(
              'tra-text-web-heading-8',
              isDark ? 'text-white print:text-neutral-900' : 'text-neutral-900',
            )}
          >
            {recipeMetaSection.title}
          </span>
          {ratingSummary && ratingSummary.average > 0 && (
            <div className="flex items-center gap-2 mt-1">
              <RatingStars value={ratingSummary.average} hideValue size="small" dark={isDark}  />
              {ratingSummary.count > 0 && (
                <span
                  className={classNames(
                    'tra-text-web-body-m',
                    isDark ? 'text-neutral-200 print:text-neutral-500' : 'text-neutral-500',
                  )}
                >
                  {ratingSummary.count}
                </span>
              )}
            </div>
          )}
          {timeDisplay && (
            <span
              className={classNames(
                'mt-1 tra-text-web-body-m',
                isDark ? 'text-neutral-200 print:text-neutral-500' : 'text-neutral-500',
              )}
            >
              {timeDisplay}
            </span>
          )}
        </div>
      </Anchor>
    </div>
  );
}
